import React, { useEffect, useMemo, useState } from 'react';
import { Chart } from 'react-google-charts';
import styled from 'styled-components';
import { LinkDD } from '../Stories/Links';
import { QuestionMarkIcon } from '../TipSheetIcons/question-mark';
import { ArrowContainer, Popover } from 'react-tiny-popover';
import Select from 'react-select';
import democratIcon from '../../assets/images/democrat-icon.png';
import republicanIcon from '../../assets/images/republican-icon.png';
import independentIcon from '../../assets/images/independent-icon.png';
import redX from '../../assets/images/red-x.svg';
import greenCheck from '../../assets/images/green-check.svg';
import grayDash from '../../assets/images/gray-dash.svg';
import { formatNumber } from './StoryUtil';

const TableWrapper = styled.div`
  padding: 2rem 0;
`;

const TableHeading = styled.h3`
  margin-block-start: 0;
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1.7;
`;

const TableDescription = styled.div`
  font-size: 0.875rem;
  line-height: 1.7;
  margin-block-start: 0;
  margin-block-end: 1rem;
`;

const TopBadge = styled.span`
  background-color: #000000;
  color: #ffffff;
  padding: 0.25rem 0.5rem;
  border-radius: 0.625rem;
  font-size: 0.625rem;
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
`;

const OrganizationSelectContainer = styled.div`
  position: relative;
  display: inline-block;
  width: 100%;

  & .select-table__control {
    border: 1px solid #3d3d3d;
    cursor: pointer;
    border-radius: 0;
  }

  & .select-table__indicator-separator {
    display: none;
  }

  & .select-table__indicator {
    color: #000000;
  }
`;

const OrganizationHeading = styled.h4`
  font-weight: 700;
  line-height: 1.7;
  margin-block: 1rem;
`;

const OrganizationTileContainer = styled.div`
  display: flex;
  gap: 1rem;
  padding-block: 1.5rem 1rem;
  flex-wrap: wrap;
`;

const OrganizationTile = styled.div`
  padding: 1rem;
  background: ${(props) => props.$background || '#fafafa'};
  border-radius: 0.25rem;
  border: 1px solid #ccc;
  width: calc(25% - 2.875rem);
  justify-content: center;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const OrganizationLarge = styled.div`
  font-size: 1.875rem;
  color: ${(props) => props.$color || '#212121'};
  font-weight: 700;
  line-height: 1.2;
  text-transform: ${(props) => (props.$color ? 'uppercase' : 'initial')};
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;

  & img {
    height: 1.25rem;
    width: 1.25rem;
  }
`;

const OrganizationSmall = styled.div`
  font-size: 0.75rem;
  line-height: 1.2;
`;

const OrganizationLink = styled.span`
  padding: 0;
  text-align: center;
  margin-block-start: auto;

  & a {
    font-size: 0.875rem;
    line-height: 1.2;
    color: #0a819f;
    font-weight: 600;

    &:hover,
    &:focus {
      text-decoration: none;
    }
  }
`;

const AdvocatingAsLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 700;
  color: #3d3d3d;
  line-height: 1.4;
`;

const AdvocatingAsList = styled.span`
  font-size: 0.75rem;
  line-height: 1.4;
  color: #212121;
`;

const ChartWrapper = styled.div`
  .google-visualization-table {
    width: 100%;
    border: 1px solid #eeeeee;

    & > div {
      scrollbar-width: thin;
    }

    &-table {
      width: 100%;
      font-size: 0.875rem;

      td {
        padding: 0.5rem;

        &:last-child {
          & a {
            font-weight: normal;
          }
        }

        a {
          color: #000000;
          font-size: 0.875rem;
          font-weight: 700;

          &:hover,
          &:focus {
            text-decoration: none;
          }
        }
      }
    }

    &-tr {
      &-even {
        background: #fafafa;

        td {
          border: 0.5rem solid #fafafa;
        }
      }

      &-odd {
        background: #ffffff;

        td {
          border: 0.5rem solid #ffffff;
        }
      }
    }

    &-tr-head {
      font-size: 0.875rem;
      font-weight: 600;
      line-height: 1.2;

      .google-visualization-table-th {
        background: #f3f9fa;
        padding: 1rem;
        text-align: left;

        &.unsorted::after {
          content: url('data:image/svg+xml;utf8,<svg width="9" height="13" viewBox="0 0 9 13" fill="none" xmlns="http://www.w3.org/2000/svg"><path opacity="0.2" d="M0.756964 7.54496H8.24304C8.91615 7.54496 9.25271 8.39073 8.77775 8.88382L5.03472 12.7698C4.73905 13.0767 4.26095 13.0767 3.96843 12.7698L0.222245 8.88382C-0.252712 8.39073 0.0838466 7.54496 0.756964 7.54496ZM8.77775 4.11618L5.03472 0.230219C4.73905 -0.0767395 4.26095 -0.0767395 3.96843 0.230219L0.222245 4.11618C-0.252712 4.60927 0.0838466 5.45504 0.756964 5.45504H8.24304C8.91615 5.45504 9.25271 4.60927 8.77775 4.11618Z" fill="black"/></svg>');
          padding-left: 0.375rem;
          vertical-align: middle;
        }
      }
    }
  }

  .google-visualization-formatters-bars {
    float: none;

    > .google-charts-bar-w {
      background: #d9d9d9;
    }

    > span {
      height: 0.5rem;

      &:last-child {
        margin-inline-end: 1rem;
      }
    }

    > .google-charts-bar-r {
      background: #be2c25;
    }

    > .google-charts-bar-b {
      background: #386f1f;
    }
  }

  .vote-icon {
    display: flex;
    align-items: center;
    gap: 0.25rem;

    span {
      font-size: 0.75rem;
      height: 0.85rem;
      font-weight: 700;
    }
  }

  .author-badge {
    color: #212121;
    padding: 0.25rem 0.5rem;
    font-size: 0.625rem;
    font-weight: 700;
    line-height: 1;
    white-space: nowrap;
    margin-inline-start: 0.5rem;
    background: #fcd70b;
    border-radius: 0.125rem;
    text-transform: uppercase;
    display: inline-flex;
    align-items: center;

    &:after {
      content: url('data:image/svg+xml;utf8,<svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7.78028 1.15894L6.84108 0.219726C6.54812 -0.073242 6.07298 -0.073242 5.78002 0.219726L4.89645 1.10332L6.89671 3.10362L7.78028 2.22003C8.07324 1.92706 8.07324 1.45207 7.78028 1.15894ZM3.92413 0.838162C3.68007 0.5941 3.28446 0.5941 3.0404 0.838162L1.18451 2.69425C1.08686 2.79191 1.08686 2.95019 1.18451 3.04769L1.5381 3.40128C1.63575 3.49893 1.79403 3.49893 1.89168 3.40128L3.48258 1.81035L3.83616 2.16378L1.4681 4.53174C0.647862 5.35201 0.13112 6.42695 0.00282587 7.57986L0.00235713 7.58345C-0.0242047 7.8222 0.177509 8.02408 0.416253 7.99767C1.57062 7.8702 2.64712 7.35327 3.46836 6.53205L6.54312 3.45722L5.24988 2.16394L3.92413 0.838162Z" fill="%23212121"/></svg>');
      display: inline-block;
      margin-left: 4px;
    }
  }
`;

const Tooltip = styled.div`
  background: #d9d9d9;
  padding: 1rem;
  max-width: 300px;
  border-radius: 0.25rem;
  margin-block-end: -1px;
  font-size: 0.625rem;
  font-style: italic;
  line-height: 1.7;
`;

const TooltipButton = styled.button`
  cursor: pointer;
  display: inline-block;
  vertical-align: middle;
  border: none;
  padding: 0;
  background: none;
`;

const OptionValue = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const OptionText = styled.span`
  max-width: calc(100% - 4rem);
  overflow: hidden;
`;

const DownloadButton = styled.button`
  padding: 0.5rem 1rem;
  background-color: #0a819f;
  color: #ffffff;
  border: none;
  border-radius: 0.25rem;
  cursor: pointer;
`;

const DownloadWrapper = styled.div`
  display: flex;
  gap: 1rem;
  margin-block-start: 1rem;
`;

const chartOptions = {
  allowHtml: true,
  showRowNumber: false,
  cssClassNames: { tableCell: 'cell', width: 'width' },
};

const chartFormatters = [
  {
    type: 'BarFormat',
    column: 3,
    options: {
      width: 200,
      min: -50,
      max: 50,
    },
    maxValue: 100,
  },
];

const chartParams = {
  view: {
    columns: [0, 1, 2, 3, 4],
  },
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
};

const range = (start, stop, step = 1) => {
  return Array.from(
    { length: Math.ceil((stop - start) / step) },
    (_, i) => start + i * step
  );
};

const formatAlignmentData = (alignmentData) => {
  const transformedData = alignmentData.map((item) => {
    // Extract last name for sorting
    const nameParts = item.full_name.split(' ');
    const lastName = nameParts[nameParts.length - 1];
    const isAuthor = item.is_author === '1';
    const legislatorLink = LinkDD(item.full_name, item.pid, 'per', null, true);
    const authorBadgeHtml = isAuthor
      ? `<div style="white-space: nowrap">${legislatorLink} <span class="author-badge">Author</span></div>`
      : legislatorLink;

    // Convert party to shorthand
    const partyShorthand = item.party.substring(0, 3).toUpperCase();
    const partyIcon =
      item.party === 'Democrat'
        ? `<img src=${democratIcon} alt="Democrat" height='24' width='24' />`
        : item.party === 'Republican'
        ? `<img src=${republicanIcon} alt="Republican" height='24' width='24' />`
        : `<img src=${independentIcon} alt="Independent" height='24' width='24' />`;

    // Fix vote spellings
    const voteFixed =
      item.legislator_vote === 'NOE'
        ? 'NO'
        : item.legislator_vote === 'ABS'
        ? 'NVR'
        : item.legislator_vote === 'AYE' || item.legislator_vote === 'YES'
        ? 'AYE'
        : item.legislator_vote;

    const voteStylized =
      item.legislator_vote === 'NOE'
        ? `<div class="vote-icon" style="color: #BE2C25;"><span>NO</span><img src=${redX} alt="Red X Icon"/></div>`
        : item.legislator_vote === 'ABS'
        ? `<div class="vote-icon" style="color: #666666;"><span>NVR</span><img src=${grayDash} alt="Gray Dash Icon"/></div>`
        : item.legislator_vote === 'AYE'
        ? `<div class="vote-icon" style="color: #386F1F;"><span>AYE</span><img src=${greenCheck} alt="Green Check Icon"/></div>`
        : isAuthor 
        ? `<div class="vote-icon" style="color: #666666;"><span>${item.legislator_vote}</span></div>`
        : `<div class="vote-icon" style="color: #666666;"><span>${item.legislator_vote}</span><img src=${grayDash} alt="Gray Dash Icon"/></div>`;

    // Format alignment score
    const alignmentScore = Number(item.alignment_score);
    const alignmentScoreFormatted = isNaN(alignmentScore)
      ? 'NO RECORD'
      : `${alignmentScore.toFixed(1)}% (${item.alignment_opp_count}) [${
          item.alignment_time_period
        }]`;
    const alignmentScoreValue = isNaN(alignmentScore) ? 0 : alignmentScore - 50;

    const [startYear, endYear] = item.contribution_time_period
      .split(' to ')
      .map(Number);

    const donationUrl = LinkDD(
      '',
      `?receiver[]=pid-${
        item.pid
      }&transaction_type[]=Candidate+Donations&transaction_type[]=Party+Committees&giver[]=oid-${
        item.oid
      }${range(startYear, endYear + 1)
        .map((year) => `&year[]=${year}`)
        .join('')}`,
      'fin',
      null,
      false,
      null,
      '',
      true
    );

    // Format donations
    let donationsFormatted;
    if (item.donations === 'N/A' || isNaN(item.donations)) {
      donationsFormatted = 'NO RECORD';
    } else {
      const donationAmount = `<a href=${donationUrl} target="_blank">${formatCurrency(
        item.donations
      )}</a>`;
      donationsFormatted = `${donationAmount} [${item.contribution_time_period}]`;
    }

    return [
      {
        v: lastName,
        f: authorBadgeHtml,
        csv: item.full_name,
      },
      {
        v: partyIcon,
        csv: partyShorthand,
      },
      {
        v: voteStylized,
        csv: voteFixed,
      },
      {
        v: alignmentScoreValue,
        f: alignmentScoreFormatted,
        csv: alignmentScoreFormatted,
      },
      {
        v: donationsFormatted,
        csv: item.donations + ' [' + item.contribution_time_period + ']',
      },
      item.name,
      item.oid,
      item.org_position,
      item.org_position_date,
      item.org_success_rate,
      item.org_success_rate_time_period,
      item.total_donated,
      item.total_donated_time_period,
    ];
  });

  const alignmentDataHeadings = [
    'Legislator',
    'Party',
    'Vote',
    {
      type: 'number',
      label: 'Alignment Score (based on # of votes) over [period]',
    },
    'Contributions [period]',
    'Organization Name',
    'Organization ID',
    'Organization Position',
    'Organization Position Date',
    'Organization Success Rate',
    'Organization Success Rate Time Period',
    'Total Donated',
    'Total Donated Time Period',
  ];

  // Add headings as the first array
  transformedData.unshift(alignmentDataHeadings);

  return transformedData;
};

const convertToCSV = (data) => {
  return data
    .map((row) =>
      row
        .map((cell) => {
          let cellValue;
          if (typeof cell === 'object' && cell.type === 'number') {
            cellValue = cell.label;
          } else if (typeof cell === 'object' && cell !== null) {
            cellValue = cell.csv;
          } else {
            cellValue = cell;
          }
          // Wrap cell value in double quotes if it contains a comma
          if (typeof cellValue === 'string' && cellValue.includes(',')) {
            cellValue = `"${cellValue}"`;
          }
          return cellValue;
        })
        .join(',')
    )
    .join('\n');
};

const downloadCSV = (data, billName) => {
  const csv = convertToCSV(data);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `Bill ${billName} Leigslator Organization Alignment.csv`;
  link.click();
  URL.revokeObjectURL(link.href);
};

export const LegislatorOrgAlignmentTable = ({ alignmentData, billName }) => {
  const transformedAlignmentData = useMemo(
    () => formatAlignmentData(alignmentData),
    [alignmentData]
  );

  // Organization unique organization values
  // Sort alphabetically for select options
  const organizationKeys = useMemo(() => {
    const uniqueNames = new Set();
    alignmentData.forEach((row) => {
      if (row.name) {
        uniqueNames.add(row.name);
      }
    });
    return [...uniqueNames].sort((a, b) => a.localeCompare(b));
  }, [alignmentData]);

  // Full organization data based on unique keys - removes any null values
  const filteredDataByOrgId = useMemo(() => {
    return organizationKeys.map((org) =>
      alignmentData.find((row) => row.name === org)
    );
  }, [alignmentData, organizationKeys]);

  // Sort filtered data by total contribution dollar amount
  const sortedDataByContribution = useMemo(() => {
    return filteredDataByOrgId.sort(
      (a, b) => (Number(b.total_donated) || 0) - (Number(a.total_donated) || 0)
    );
  }, [filteredDataByOrgId]);

  // Default selected value - highest total contribution
  const defaultSelected =
    {
      value: sortedDataByContribution[0]?.name,
      label: (
        <OptionValue>
          <OptionText>{sortedDataByContribution[0]?.name}</OptionText>
          {sortedDataByContribution[0]?.total_donated !== 'N/A' && (
            <TopBadge>Top Donor</TopBadge>
          )}
        </OptionValue>
      ),
    } || '';

  const [selected, setSelected] = useState(defaultSelected);
  const [filteredDatas, setFilteredDatas] = useState([]);
  const [isPopoverOpen, setPopoverOpen] = useState('');

  // advocating_as names for the selected organization (up to 3)
  const advocatingAs = useMemo(() => {
    if (!selected?.value) return [];
    const row = alignmentData.find((r) => r.name === selected.value);
    if (!row?.advocating_as) return [];
    const raw = row.advocating_as;
    const list = Array.isArray(raw) ? raw : typeof raw === 'string' ? [raw] : [];
    return list.slice(0, 3);
  }, [selected, alignmentData]);

  useEffect(() => {
    if (!transformedAlignmentData?.length || !selected?.value) {
      setFilteredDatas([]);
      return;
    }

    const matchingOrganizationKeys = [
      transformedAlignmentData[0],
      ...transformedAlignmentData.filter((row) =>
        row?.includes(selected.value)
      ),
    ];
    setFilteredDatas(matchingOrganizationKeys);
  }, [selected, transformedAlignmentData]);

  return (
    <TableWrapper>
      <TableHeading>Legislator-Organization Relationships</TableHeading>
      <TableDescription>
        The drop-down menu includes organizations that took a position on this
        bill. The chart displays that organization's relationship with
        legislators in two ways:
        <ol>
          <li>
            An alignment score that shows how often the legislator votes
            consistent with the organization's agenda
          </li>
          <li>How much money the organization has given to the legislator</li>
        </ol>
      </TableDescription>
      <div>
        <OrganizationHeading>Organization</OrganizationHeading>
        <OrganizationSelectContainer>
          {organizationKeys.length > 0 ? (
            <Select
              classNamePrefix="select-table"
              defaultValue={selected}
              name="select-table"
              isSearchable={true}
              options={organizationKeys.map((opt) => ({
                value: opt,
                label:
                  opt === defaultSelected.value ? defaultSelected.label : opt,
              }))}
              onChange={(e) => setSelected(e)}
            />
          ) : null}
        </OrganizationSelectContainer>
      </div>
      {/* filterDatas is an array of arrays reference alignmentDataHeadings for nested value meaning */}
      {filteredDatas.length > 0 && filteredDatas?.[1] ? (
        <OrganizationTileContainer>
          <OrganizationTile>
            <span>
              Position{' '}
              <Popover
                isOpen={isPopoverOpen === 'position'}
                positions={['top', 'bottom', 'left', 'right']}
                onClickOutside={() => setPopoverOpen('')}
                content={({ position, childRect, popoverRect }) => (
                  <ArrowContainer
                    position={position}
                    childRect={childRect}
                    popoverRect={popoverRect}
                    arrowColor={'#D9D9D9'}
                    arrowSize={16}
                  >
                    <Tooltip>
                      The recorded position this organization took on this bill
                    </Tooltip>
                  </ArrowContainer>
                )}
              >
                <TooltipButton
                  type="button"
                  onClick={() => setPopoverOpen('position')}
                >
                  <QuestionMarkIcon />
                </TooltipButton>
              </Popover>
            </span>
            {/* org_position */}
            <OrganizationLarge
              $color={filteredDatas[1][7] === 'For' ? '#386f1f' : '#be2c25'}
            >
              {filteredDatas[1][7] || 'N/A'}
              {filteredDatas[1][7] === 'For' ? (
                <img src={greenCheck} alt="Green Check Icon" />
              ) : filteredDatas[1][7] === 'Against' ? (
                <img src={redX} alt="Red X Icon" />
              ) : null}
            </OrganizationLarge>
            {/* org_position_date */}
            <OrganizationSmall>
              {filteredDatas[1][8]
                ? `Recorded on ${filteredDatas[1][8]}`
                : 'No date recorded'}
            </OrganizationSmall>
          </OrganizationTile>
          <OrganizationTile>
            <span>
              Success Rate{' '}
              <Popover
                isOpen={isPopoverOpen === 'successRate'}
                positions={['top', 'bottom', 'left', 'right']}
                onClickOutside={() => setPopoverOpen('')}
                content={({ position, childRect, popoverRect }) => (
                  <ArrowContainer
                    position={position}
                    childRect={childRect}
                    popoverRect={popoverRect}
                    arrowColor={'#D9D9D9'}
                    arrowSize={16}
                  >
                    <Tooltip>
                      Overall success rate this organization has in passing its
                      agenda through the Legislature during the designated time
                      period
                    </Tooltip>
                  </ArrowContainer>
                )}
              >
                <TooltipButton
                  type="button"
                  onClick={() => setPopoverOpen('successRate')}
                >
                  <QuestionMarkIcon />
                </TooltipButton>
              </Popover>
            </span>
            <OrganizationLarge>
              {/* org_success_rate */}
              {filteredDatas[1][9]
                ? `${Number(filteredDatas[1][9]).toFixed(1)}%`
                : 'N/A'}
            </OrganizationLarge>
            {/* org_success_rate_time_period */}
            <OrganizationSmall>
              {filteredDatas[1][10] || 'No time period available'}
            </OrganizationSmall>
          </OrganizationTile>
          {/* total_donated */}
          {Number(filteredDatas[1][11]) > 0 ? (
            <OrganizationTile>
              <span>
                Total Contributions{' '}
                <Popover
                  isOpen={isPopoverOpen === 'totalContributions'}
                  positions={['top', 'bottom', 'left', 'right']}
                  onClickOutside={() => setPopoverOpen('')}
                  content={({ position, childRect, popoverRect }) => (
                    <ArrowContainer
                      position={position}
                      childRect={childRect}
                      popoverRect={popoverRect}
                      arrowColor={'#D9D9D9'}
                      arrowSize={16}
                    >
                      <Tooltip>
                        Total amount of money (identified by Digital Democracy)
                        this organization has donated to legislators
                      </Tooltip>
                    </ArrowContainer>
                  )}
                >
                  <TooltipButton
                    type="button"
                    onClick={() => setPopoverOpen('totalContributions')}
                  >
                    <QuestionMarkIcon />
                  </TooltipButton>
                </Popover>
              </span>
              {/* total_donated */}
              <OrganizationLarge>
                {formatNumber(Number(filteredDatas[1][11] || 0))}
              </OrganizationLarge>
              {/* total_donated_time_period */}
              <OrganizationSmall>
                {filteredDatas[1][12] || 'No time period available'}
              </OrganizationSmall>
            </OrganizationTile>
          ) : null}
          <OrganizationTile $background="#ffffff">
            {advocatingAs.length > 0 && (
              <>
                <AdvocatingAsLabel>Advocating as:</AdvocatingAsLabel>
                <AdvocatingAsList>{advocatingAs.join(', ')}</AdvocatingAsList>
              </>
            )}
            {/* oid */}
            {filteredDatas[1][6] ? (
              <OrganizationLink>
                {LinkDD(
                  'More about this organization',
                  filteredDatas[1][6],
                  'org',
                  null,
                  false
                )}
              </OrganizationLink>
            ) : null}
          </OrganizationTile>
        </OrganizationTileContainer>
      ) : null}
      <ChartWrapper>
        {filteredDatas?.length > 0 ? (
          <Chart
            loader={<div>Loading Chart</div>}
            chartType="Table"
            width="100%"
            height="600px"
            data={filteredDatas}
            options={chartOptions}
            formatters={chartFormatters}
            chartWrapperParams={chartParams}
            controls={[]}
          />
        ) : (
          <div>No data available</div>
        )}
      </ChartWrapper>
      <DownloadWrapper>
        {filteredDatas?.length > 0 ? (
          <DownloadButton onClick={() => downloadCSV(filteredDatas, billName)}>
            Download Single Organization
          </DownloadButton>
        ) : null}
        {transformedAlignmentData?.length > 0 ? (
          <DownloadButton
            onClick={() => downloadCSV(transformedAlignmentData, billName)}
          >
            Download All Organizations
          </DownloadButton>
        ) : null}
      </DownloadWrapper>
    </TableWrapper>
  );
};
